"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import type { CreateTypes as ConfettiInstance, Options as ConfettiOptions } from "canvas-confetti"
import ReactCanvasConfetti from "react-canvas-confetti"

import { ConfettiStyle } from "@/hooks/use-confetti-cable"

// Geist -600 color variants from globals.css (converted from oklch to hex)
const CONFETTI_COLORS = [
  "#3B82F6", // geist-blue-600
  "#DC2626", // geist-red-600
  "#D97706", // geist-amber-600
  "#16A34A", // geist-green-600
  "#0D9488", // geist-teal-600
  "#9333EA", // geist-purple-600
  "#DB2777", // geist-pink-600
]

interface ConfettiCanvasProps {
  style: ConfettiStyle | null
  onComplete: () => void
}

export function ConfettiCanvas({ style, onComplete }: ConfettiCanvasProps) {
  const confettiRef = useRef<ConfettiInstance | null>(null)
  const [isAnimating, setIsAnimating] = useState(false)

  const onInit = useCallback(({ confetti }: { confetti: ConfettiInstance }) => {
    confettiRef.current = confetti
  }, [])

  const fire = useCallback((options: ConfettiOptions) => {
    if (confettiRef.current) {
      confettiRef.current(options)
    }
  }, [])

  const fireRaining = useCallback(() => {
    // Fire multiple waves of confetti across the screen
    const positions = [0.1, 0.25, 0.4, 0.5, 0.6, 0.75, 0.9]

    // First wave
    positions.forEach((x) => {
      fire({
        particleCount: 150,
        spread: 140,
        origin: { x, y: -0.1 },
        angle: 90,
        startVelocity: 55,
        gravity: 1.8,
        ticks: 180,
        decay: 0.92,
        colors: CONFETTI_COLORS,
        scalar: 1.2,
      })
    })

    // Second wave after a short delay
    setTimeout(() => {
      positions.forEach((x) => {
        fire({
          particleCount: 100,
          spread: 120,
          origin: { x, y: -0.1 },
          angle: 90,
          startVelocity: 45,
          gravity: 1.7,
          ticks: 150,
          decay: 0.92,
          colors: CONFETTI_COLORS,
          scalar: 1.0,
        })
      })
    }, 200)

    setTimeout(onComplete, 2000)
  }, [fire, onComplete])

  const fireFireworks = useCallback(() => {
    // Evenly spaced positions around the edges of the screen
    const positions = [
      { x: 0.15, y: 0.2 }, // top left
      { x: 0.85, y: 0.25 }, // top right
      { x: 0.1, y: 0.55 }, // middle left
      { x: 0.9, y: 0.5 }, // middle right
      { x: 0.2, y: 0.8 }, // bottom left
      { x: 0.8, y: 0.75 }, // bottom right
    ]

    let count = 0

    const burst = () => {
      if (count >= positions.length) {
        setTimeout(onComplete, 2000)
        return
      }

      const { x, y } = positions[count]

      fire({
        particleCount: 60,
        spread: 360,
        origin: { x, y },
        startVelocity: 35,
        gravity: 0.8,
        ticks: 120,
        colors: CONFETTI_COLORS,
        scalar: 1.1,
      })

      count++
      setTimeout(burst, 250)
    }

    burst()
  }, [fire, onComplete])

  const fireCannons = useCallback(() => {
    const duration = 1500
    const end = Date.now() + duration

    const frame = () => {
      // Left cannon - higher position, angled toward center
      fire({
        particleCount: 3,
        angle: 55,
        spread: 35,
        origin: { x: 0, y: 0.5 },
        startVelocity: 75,
        gravity: 1,
        ticks: 200,
        colors: CONFETTI_COLORS,
        scalar: 1.1,
      })

      // Right cannon - higher position, angled toward center
      fire({
        particleCount: 3,
        angle: 125,
        spread: 35,
        origin: { x: 1, y: 0.5 },
        startVelocity: 75,
        gravity: 1,
        ticks: 200,
        colors: CONFETTI_COLORS,
        scalar: 1.1,
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      } else {
        setTimeout(onComplete, 2000)
      }
    }

    frame()
  }, [fire, onComplete])

  useEffect(() => {
    if (!style || isAnimating) return

    setIsAnimating(true)

    switch (style) {
      case "raining":
        fireRaining()
        break
      case "fireworks":
        fireFireworks()
        break
      case "cannons":
        fireCannons()
        break
    }
  }, [style, isAnimating, fireRaining, fireFireworks, fireCannons])

  useEffect(() => {
    if (!style) {
      setIsAnimating(false)
    }
  }, [style])

  return (
    <ReactCanvasConfetti
      onInit={onInit}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9999,
      }}
      globalOptions={{ resize: true, useWorker: true }}
    />
  )
}
